import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight, CheckCircle, Leaf, Award, ShieldCheck, Utensils, GraduationCap, Package, Sparkles
} from 'lucide-react';
import { horecaBrands } from '../utils/mockData';
import { pushEvent } from '../utils/analytics';

// WhatsApp Business number (approved): +91 99993 07127
const WA_TRADE_URL =
  'https://wa.me/919999307127?text=' +
  encodeURIComponent(
    "Hi NoBelly team, I'm interested in listing NoBelly Bier at my outlet. Please share the HORECA partnership details."
  );

// Inline WhatsApp glyph — lucide has no WhatsApp mark and no FontAwesome CDN
// is loaded on the site; keeping the icon inline avoids adding a dependency.
const WhatsAppIcon = ({ size = 16 }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="currentColor"
    aria-hidden="true"
    focusable="false"
  >
    <path d="M20.52 3.48A11.85 11.85 0 0012.06 0C5.53 0 .24 5.29.24 11.82c0 2.08.54 4.11 1.57 5.9L0 24l6.44-1.7a11.84 11.84 0 005.62 1.43h.01c6.53 0 11.82-5.29 11.82-11.82 0-3.16-1.23-6.13-3.37-8.43zM12.07 21.5h-.01a9.7 9.7 0 01-4.94-1.36l-.36-.21-3.82 1.01 1.02-3.72-.24-.38a9.66 9.66 0 01-1.5-5.16c0-5.35 4.36-9.7 9.72-9.7 2.6 0 5.03 1.01 6.87 2.84a9.63 9.63 0 012.85 6.87c-.01 5.35-4.37 9.71-9.72 9.71zm5.32-7.28c-.29-.14-1.72-.85-1.98-.95-.27-.1-.46-.14-.66.14-.19.29-.75.94-.92 1.14-.17.19-.34.22-.63.07-.29-.14-1.23-.45-2.34-1.44-.87-.77-1.45-1.72-1.62-2.01-.17-.29-.02-.44.13-.58.13-.13.29-.34.44-.51.14-.17.19-.29.29-.48.1-.19.05-.36-.02-.51-.07-.14-.66-1.59-.9-2.17-.24-.57-.48-.49-.66-.5-.17-.01-.36-.01-.55-.01-.19 0-.51.07-.77.36-.27.29-1.02.99-1.02 2.42 0 1.43 1.04 2.81 1.19 3 .14.19 2.05 3.13 4.97 4.39.7.3 1.24.48 1.66.61.7.22 1.33.19 1.84.11.56-.08 1.72-.7 1.97-1.38.24-.68.24-1.26.17-1.38-.07-.12-.26-.19-.55-.34z"/>
  </svg>
);
import usePageMeta from '../hooks/usePageMeta';

const API_BASE = '';

const HERO_BOTTLE = 'https://customer-assets-m6fa6gv7.emergentagent.net/job_044e7215-b684-4e8a-bc75-b8b1576b06e1/artifacts/1vxmk0tt_ChatGPT%20Image%20Aug%2023%2C%202026%2C%2012_48_37%20AM.png';

const OUTLET_TYPES = [
  { value: '', label: 'Outlet type…' },
  { value: 'hotel', label: 'Hotel' },
  { value: 'restaurant', label: 'Restaurant' },
  { value: 'bar', label: 'Bar / Lounge' },
  { value: 'club', label: 'Club' },
  { value: 'cafe', label: 'Café' },
  { value: 'retail', label: 'Retail Store' },
  { value: 'other', label: 'Other' },
];

const WHY_POINTS = [
  { icon: Sparkles, title: 'DISTINCTIVE LOW-CALORIE POSITIONING', desc: 'A clean, low-calorie lager that stands apart on your menu.' },
  { icon: Leaf, title: 'CLEAN LABEL', desc: 'No added sugar. No glycerine. No artificial preservatives.' },
  { icon: Award, title: 'VLB BERLIN RECIPE', desc: 'Recipe developed in partnership with VLB Berlin — one of the world\u2019s most respected brewing institutes.' },
  { icon: Utensils, title: 'FOOD-FRIENDLY LAGER PROFILE', desc: 'Balanced, crisp and easy to pair — designed to sit alongside a full menu.' },
  { icon: Package, title: 'PREMIUM 330 ML PACKAGING', desc: 'Highly visible, premium 330 ml format built for on-premise pours.' },
  { icon: GraduationCap, title: 'STAFF TRAINING & POSM', desc: 'Tasting sessions, menu support, staff briefing decks and point-of-sale materials.' },
];

// Activations — image files exist only for Holi (2) and Harley (4).
// Pickleball & DoubleTree are text-forward until verified photos are supplied.
const ACTIVATIONS = [
  {
    slug: 'holi-dlf-club-5',
    kind: 'photo',
    title: 'Holi at DLF Club 5',
    venue: 'DLF Club 5',
    city: 'Gurgaon',
    type: 'Festival Activation',
    images: ['/events/holi1.jpg', '/events/holi2.jpg'],
  },
  {
    slug: 'harley-owners-group',
    kind: 'photo',
    title: 'Harley Owners Group Meet',
    venue: 'Members\u2019 ride-in',
    city: 'Gurgaon',
    type: 'Lifestyle · Community Pour',
    images: ['/events/harley1.jpg', '/events/harley2.jpg', '/events/harley3.jpg'],
  },
  {
    slug: 'pickleball-tournament',
    kind: 'photo',
    title: 'Pickleball Tournament',
    venue: 'Community sports weekend',
    city: 'Gurgaon',
    type: 'Sports Sponsorship',
    images: ['/events/harley4.jpg'],
  },
];

const Horeca = () => {
  usePageMeta({
    title: 'For Trade | Stock No-Belly Bier at Your Outlet',
    description: 'Retail partners, bars, restaurants and hotels — join the No-Belly network. Clean-brewed lager with staff training, POSM, tasting sessions and activation support.',
    path: '/horeca',
  });

  const [form, setForm] = useState({
    contact_name: '', outlet_name: '', outlet_type: '', city: '',
    phone: '', email: '', number_of_outlets: 1, message: '',
    consent: false, lead_source: 'horeca_page', website: '',
  });
  const [state, setState] = useState({ loading: false, success: false, error: '' });
  const [showSticky, setShowSticky] = useState(false);
  // Ensures horeca_form_started fires exactly once per page mount.
  const formStartedRef = useRef(false);
  const formRef = useRef(null);

  const onFormStart = () => {
    if (formStartedRef.current) return;
    formStartedRef.current = true;
    pushEvent('horeca_form_started', {
      form_name: 'horeca_partnership',
      page_path: '/horeca',
    });
  };

  useEffect(() => {
    const onScroll = () => setShowSticky(window.scrollY > 500);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Capture lead source from URL param if present (e.g. utm_source, ref)
  useEffect(() => {
    const p = new URLSearchParams(window.location.search);
    const source = p.get('utm_source') || p.get('ref');
    if (source) setForm(f => ({ ...f, lead_source: `horeca_page:${source.slice(0, 40)}` }));
  }, []);

  const scrollToForm = (e) => {
    if (e) e.preventDefault();
    if (formRef.current) formRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.consent) { setState({ loading: false, success: false, error: 'Please accept the privacy consent to continue.' }); return; }
    if (!form.outlet_type) { setState({ loading: false, success: false, error: 'Please pick an outlet type.' }); return; }
    setState({ loading: true, success: false, error: '' });
    try {
      const payload = { ...form, number_of_outlets: parseInt(form.number_of_outlets, 10) || 1 };
      const res = await fetch(`${API_BASE}/api/horeca/inquiry`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        const d = body.detail;
        const msg = Array.isArray(d) ? d.map(x => x.msg || 'Invalid').join(' · ') : (d || 'Something went wrong.');
        throw new Error(msg);
      }
      setState({ loading: false, success: true, error: '' });
      // Fire the submission event ONLY after the backend confirms success.
      pushEvent('horeca_form_submitted', {
        form_name: 'horeca_partnership',
        page_path: '/horeca',
        submission_status: 'success',
      });
      setForm({
        contact_name: '', outlet_name: '', outlet_type: '', city: '',
        phone: '', email: '', number_of_outlets: 1, message: '',
        consent: false, lead_source: form.lead_source, website: '',
      });
    } catch (err) {
      setState({ loading: false, success: false, error: err.message });
    }
  };

  return (
    <main className="nb-page nb-page-horeca" data-testid="page-horeca">
      {/* HERO */}
      <section className="nb-page-hero nb-horeca-hero" data-testid="horeca-hero">
        <div className="nb-page-hero-inner nb-horeca-hero-grid">
          <div className="nb-horeca-hero-copy">
            <p className="nb-eyebrow">FOR TRADE · HORECA</p>
            <h1 className="nb-display-md">A LIGHTER POUR<br />FOR MODERN MENUS.</h1>
            <p className="nb-page-lede">
              Clean-brewed beer, premium positioning and on-ground support for hospitality partners.
            </p>
            <button type="button" onClick={scrollToForm} className="nb-cta-primary nb-cta-lg" data-testid="horeca-hero-cta">
              BECOME A NO-BELLY PARTNER<ArrowRight size={18} aria-hidden="true" />
            </button>
          </div>
          <div className="nb-horeca-hero-visual" aria-hidden="true">
            <img
              src={HERO_BOTTLE}
              alt=""
              width="360"
              height="480"
              loading="eager"
              fetchpriority="high"
              decoding="async"
            />
          </div>
        </div>
      </section>

      {/* WHY IT WORKS */}
      <section className="nb-horeca-why" data-testid="horeca-why">
        <header className="nb-section-head">
          <p className="nb-eyebrow">WHY IT WORKS ON-PREMISE</p>
          <h2 className="nb-display-md">A LAGER YOUR TEAM<br />CAN GET BEHIND.</h2>
        </header>
        <div className="nb-horeca-why-grid">
          {WHY_POINTS.map((p, i) => (
            <div key={i} className="nb-why-tile" data-testid={`horeca-why-${i}`}>
              <p.icon size={24} className="nb-why-icon" />
              <h3 className="nb-why-title">{p.title}</h3>
              <p className="nb-copy">{p.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* PARTNER PROOF */}
      <section className="nb-horeca-partners" data-testid="horeca-partners">
        <header className="nb-section-head">
          <p className="nb-eyebrow">PARTNER PROOF</p>
          <h2 className="nb-display-md">POURING AT.</h2>
          <p className="nb-copy" style={{ margin: '0 auto' }}>
            A selection of current HORECA partners across Gurgaon.
          </p>
        </header>
        <div className="nb-horeca-grid" data-testid="horeca-partner-grid">
          {horecaBrands.map(b => (
            <div key={b.id} className="nb-horeca-tile" data-testid={`horeca-partner-${b.slug}`}>
              <img src={b.logo} alt={b.name} loading="lazy" />
            </div>
          ))}
        </div>
        <p className="nb-microcopy">Logos shown with partner permission. No logo has been recreated or restyled.</p>
      </section>

      {/* ACTIVATIONS */}
      <section className="nb-horeca-activations" data-testid="horeca-activations">
        <header className="nb-section-head">
          <p className="nb-eyebrow">ACTIVATIONS</p>
          <h2 className="nb-display-md">EVENT &amp; ACTIVATION SUPPORT.</h2>
          <p className="nb-copy" style={{ margin: '0 auto' }}>
            A few recent on-ground activations with our HORECA partners.
          </p>
        </header>
        <div className="nb-activations-grid">
          {ACTIVATIONS.map(a => (
            <article key={a.slug} className={`nb-activation-card ${a.kind === 'text' ? 'text' : ''}`} data-testid={`activation-${a.slug}`}>
              {a.kind === 'photo' ? (
                <div className="nb-activation-photos">
                  {a.images.map((src, j) => (
                    <div key={src} className={`nb-activation-img ${a.images.length >= 3 && j === 0 ? 'lead' : ''} ${a.images.length === 2 ? 'half' : ''}`}>
                      <img src={src} alt={`${a.title} ${j + 1}`} loading="lazy" />
                    </div>
                  ))}
                </div>
              ) : (
                <div className="nb-activation-plate" aria-hidden="true">
                  <span className="nb-activation-plate-mark">NB</span>
                  <span className="nb-activation-plate-note">{a.note}</span>
                </div>
              )}
              <div className="nb-activation-body">
                <p className="nb-eyebrow">{a.type} · {a.city}</p>
                <h3 className="nb-display-sm">{a.title}</h3>
                <p className="nb-copy"><strong>Venue:</strong> {a.venue}</p>
              </div>
            </article>
          ))}
        </div>
      </section>

      {/* INQUIRY FORM */}
      <section className="nb-horeca-form-section" ref={formRef} id="partner-form" data-testid="horeca-form-section">
        <div className="nb-horeca-form-copy">
          <p className="nb-eyebrow">BECOME A PARTNER</p>
          <h2 className="nb-display-sm">TELL US ABOUT YOUR OUTLET.</h2>
          <p className="nb-copy">
            Our HORECA team responds within one business day with pricing, samples and a partnership brief.
          </p>
          <p className="nb-copy">
            Prefer email? Write to <a href="mailto:contact@nobelly.in" className="nb-inline-link">contact@nobelly.in</a>.
          </p>
          <a
            href={WA_TRADE_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="nb-cta-ghost nb-cta-whatsapp"
            aria-label="Chat with the NoBelly HORECA trade team on WhatsApp"
            data-testid="horeca-whatsapp-cta"
            onClick={() => pushEvent('whatsapp_click', {
              button_location: 'horeca_form',
              contact_type: 'trade',
              page_path: (typeof window !== 'undefined' ? window.location.pathname : '/horeca'),
            })}
          >
            <WhatsAppIcon size={16} /> WHATSAPP TRADE TEAM
          </a>
        </div>

        <form className="nb-inquiry-form" onSubmit={submit} onFocus={onFormStart} noValidate data-testid="horeca-form">
          <input
            type="text" name="website" tabIndex="-1" autoComplete="off"
            value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })}
            style={{ position: 'absolute', left: '-10000px', width: '1px', height: '1px', opacity: 0 }} aria-hidden="true"
          />
          {state.success ? (
            <div className="nb-form-success" data-testid="horeca-form-success">
              <CheckCircle size={22} /> Thanks — our HORECA team will reach out within one business day.
            </div>
          ) : (
            <>
              <input type="text" placeholder="Contact name" required minLength={2} maxLength={80}
                name="contact_name" autoComplete="name" aria-label="Contact name"
                value={form.contact_name}
                onChange={(e) => setForm({ ...form, contact_name: e.target.value })}
                data-testid="horeca-contact-name" />
              <input type="text" placeholder="Outlet / company name" required minLength={2} maxLength={120}
                name="outlet_name" autoComplete="organization" aria-label="Outlet or company name"
                value={form.outlet_name}
                onChange={(e) => setForm({ ...form, outlet_name: e.target.value })}
                data-testid="horeca-outlet-name" />
              <div className="nb-inquiry-row">
                <select
                  required
                  name="outlet_type"
                  value={form.outlet_type}
                  onChange={(e) => setForm({ ...form, outlet_type: e.target.value })}
                  data-testid="horeca-outlet-type"
                  className="nb-inquiry-select"
                  aria-label="Outlet type"
                >
                  {OUTLET_TYPES.map(t => (
                    <option key={t.value} value={t.value} disabled={t.value === ''}>{t.label}</option>
                  ))}
                </select>
                <input type="text" placeholder="City" required minLength={2} maxLength={60}
                  name="city" autoComplete="address-level2" aria-label="City"
                  value={form.city}
                  onChange={(e) => setForm({ ...form, city: e.target.value })}
                  data-testid="horeca-city" />
              </div>
              <div className="nb-inquiry-row">
                <input type="tel" placeholder="Phone" required minLength={7} maxLength={25}
                  name="phone" autoComplete="tel" aria-label="Phone"
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  data-testid="horeca-phone" />
                <input type="email" placeholder="Email" required
                  name="email" autoComplete="email" aria-label="Email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  data-testid="horeca-email" />
              </div>
              <input
                type="number" min="1" max="9999" step="1"
                name="number_of_outlets" aria-label="Number of outlets"
                placeholder="Number of outlets"
                required
                value={form.number_of_outlets}
                onChange={(e) => setForm({ ...form, number_of_outlets: e.target.value })}
                data-testid="horeca-number-of-outlets"
              />
              <textarea placeholder="Tell us about your outlet — footfall, current beer program, target launch date (optional)"
                name="message" aria-label="Message"
                maxLength={800} value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                data-testid="horeca-message" />
              <label className="nb-consent">
                <input type="checkbox" required checked={form.consent}
                  onChange={(e) => setForm({ ...form, consent: e.target.checked })}
                  data-testid="horeca-consent" />
                <span>I agree to the <Link to="/privacy" className="nb-inline-link">Privacy Policy</Link>.</span>
              </label>
              {state.error && <p className="nb-form-error" role="alert" data-testid="horeca-error">{state.error}</p>}
              <button type="submit" className="nb-cta-primary" disabled={state.loading} data-testid="horeca-submit">
                {state.loading ? 'Sending…' : 'Submit Inquiry'}{!state.loading && <ArrowRight size={16} />}
              </button>
            </>
          )}
        </form>
      </section>

      {/* Floating desktop / sticky mobile CTA — visible after hero */}
      <div className={`nb-horeca-sticky ${showSticky ? 'is-visible' : ''}`} data-testid="horeca-sticky-cta" aria-hidden={!showSticky}>
        <button type="button" onClick={scrollToForm} className="nb-cta-primary" data-testid="horeca-sticky-cta-btn">
          PARTNER WITH US<ArrowRight size={16} />
        </button>
      </div>
    </main>
  );
};

export default Horeca;
